import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  TextField,
  Alert,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Divider,
  Card,
  CardContent,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Fade,
  Tooltip,
  alpha,
  Chip,
} from '@mui/material';
import { Close, Inventory, Add, Refresh, Link as LinkIcon, OpenInNew, AutoAwesome, Edit, Settings } from '@mui/icons-material';
import { DialogActions, Button, CircularProgress, LinearProgress } from '@mui/material';
import PageHeader from '../components/common/PageHeader';
import DataTable from '../components/common/DataTable';
import FormDialog from '../components/common/FormDialog';
import ConfirmDialog from '../components/common/ConfirmDialog';
import StatusBadge from '../components/common/StatusBadge';
import SearchInput from '../components/common/SearchInput';
import SpecsEditorDialog from '../components/common/SpecsEditorDialog';
import api from '../services/api';

const initialFormData = {
  sku: '',
  prodnum: '',
  title: '',
  fullTitle: '',
  brand: '',
  retailId: '',
  categoryId: '',
  lang: 'ES',
  ranking: '',
  reviewsCount: 0,
  isActive: true,
};

const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [retails, setRetails] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [saving, setSaving] = useState(false);
  const [filterRetail, setFilterRetail] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [enrichDialogOpen, setEnrichDialogOpen] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [enrichResult, setEnrichResult] = useState(null);
  const [specsEditorOpen, setSpecsEditorOpen] = useState(false);
  const [savingSpecs, setSavingSpecs] = useState(false);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  
  // Debounce timer ref
  const searchTimeoutRef = useRef(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Fetch retails and categories (only once)
  const fetchMetadata = useCallback(async () => {
    try {
      const [retailsRes, categoriesRes] = await Promise.all([
        api.get('/retails'),
        api.get('/categories'),
      ]);
      setRetails(retailsRes.data);
      setCategories(categoriesRes.data);
    } catch (err) {
      setError('Error al cargar datos');
    }
  }, []);

  // Fetch products with pagination
  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', rowsPerPage.toString());
      if (debouncedSearch) params.append('search', debouncedSearch);
      if (filterRetail) params.append('retailId', filterRetail);
      if (filterCategory) params.append('categoryId', filterCategory);
      
      const productsRes = await api.get(`/products?${params.toString()}`);
      setProducts(productsRes.data.data);
      setTotalCount(productsRes.data.total);
    } catch (err) {
      setError('Error al cargar productos');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, debouncedSearch, filterRetail, filterCategory]);

  // Debounce search input
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0); // Reset to first page on search change
    }, 300);
    
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  // Initial load of metadata
  useEffect(() => {
    fetchMetadata();
  }, [fetchMetadata]);

  // Fetch products when pagination/filters change
  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);
  
  // Helper to refresh products
  const fetchData = useCallback(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleOpenDialog = (product = null) => {
    if (product) {
      // Use feedData or catalogData if available, fallback to direct properties
      const feedData = product.feedData || {};
      const catalogData = product.catalogData || {};
      
      setFormData({
        sku: product.sku || '',
        prodnum: product.prodnum || feedData.gtin || '',
        title: feedData.title || catalogData.name || product.title || '',
        fullTitle: product.fullTitle || catalogData.name || '',
        brand: feedData.brand || product.brand || '',
        retailId: product.retailId || '',
        categoryId: product.categoryId || '',
        lang: product.lang || catalogData.language || 'ES',
        ranking: product.ranking || '',
        reviewsCount: product.reviewsCount || 0,
        isActive: product.isActive !== undefined ? product.isActive : true,
      });
      setSelectedProduct(product);
    } else {
      setFormData({
        ...initialFormData,
        retailId: filterRetail || '',
        categoryId: filterCategory || '',
      });
      setSelectedProduct(null);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setFormData(initialFormData);
    setSelectedProduct(null);
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError('');

    try {
      const payload = {
        ...formData,
        ranking: formData.ranking || null,
        reviewsCount: parseInt(formData.reviewsCount) || 0,
      };

      if (selectedProduct) {
        await api.put(`/products/${selectedProduct.id}`, payload);
        setSuccess('Producto actualizado exitosamente');
      } else {
        await api.post('/products', payload);
        setSuccess('Producto creado exitosamente');
      }
      handleCloseDialog();
      fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar producto');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/products/${selectedProduct.id}`);
      setDeleteDialogOpen(false);
      setSelectedProduct(null);
      setSuccess('Producto eliminado exitosamente');
      fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al eliminar producto');
    }
  };

  // Get count of products that can be enriched
  const getEnrichableCount = () => {
    if (!filterRetail) return 0;
    return products.filter(p => p.retailId === filterRetail && !p.hasEnrichment).length;
  };

  const handleOpenEnrichDialog = () => {
    setEnrichResult(null);
    setEnrichDialogOpen(true);
  };

  const handleEnrichProducts = async () => {
    if (!filterRetail) return;
    
    setEnriching(true);
    setError('');
    
    try {
      const response = await api.post('/products/enrich', {
        retailId: filterRetail,
        forceReenrich: false
      });
      
      setEnrichResult(response.data);
      
      if (response.data.enriched > 0) {
        setSuccess(`Se enriquecieron ${response.data.enriched} productos con datos del catálogo`);
        fetchData(); // Refresh the list
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Error al enriquecer productos');
      setEnrichDialogOpen(false);
    } finally {
      setEnriching(false);
    }
  };

  // Handle saving specs from the specs editor
  const handleSaveSpecs = async (specs) => {
    if (!selectedProduct) return;
    
    setSavingSpecs(true);
    setError('');
    
    try {
      await api.patch(`/products/${selectedProduct.id}/specs`, { specs });
      
      // Update the selected product in state
      setSelectedProduct(prev => ({
        ...prev,
        specs
      }));
      
      // Update in products list
      setProducts(prev => prev.map(p => 
        p.id === selectedProduct.id ? { ...p, specs } : p
      ));
      
      setSuccess('Especificaciones guardadas exitosamente');
      setSpecsEditorOpen(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar especificaciones');
    } finally {
      setSavingSpecs(false);
    }
  };

  const getRetailName = (retailId) => {
    const retail = retails.find(r => r.id === retailId);
    return retail?.name || '-';
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category?.name || '-';
  };

  const filteredCategoriesForRetail = filterRetail
    ? categories.filter(c => c.retailId === filterRetail)
    : categories;

  const columns = [
    {
      header: 'Imagen',
      accessor: 'feedData',
      render: (value, row) => {
        const imageUrl = value?.image || row.catalogData?.catalogImage;
        return imageUrl ? (
          <Box
            component="img"
            src={imageUrl}
            alt={value?.title || 'Producto'}
            sx={{ 
              width: 50, 
              height: 50, 
              objectFit: 'contain',
              borderRadius: 1,
              bgcolor: 'grey.100'
            }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        ) : (
          <Box sx={{ width: 50, height: 50, bgcolor: 'grey.200', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Inventory sx={{ color: 'grey.400', fontSize: 20 }} />
          </Box>
        );
      },
    },
    { header: 'SKU', accessor: 'sku', minWidth: 100 },
    {
      header: 'Título',
      accessor: 'feedData',
      maxWidth: 280,
      render: (value, row) => {
        const title = value?.title || row.catalogData?.name || row.title || '-';
        const brand = value?.brand || row.brand;
        return (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {title}
            </Typography>
            {brand && (
              <Typography variant="caption" color="text.secondary">
                {brand}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      header: 'Retail',
      accessor: 'retailId',
      render: (value) => getRetailName(value),
    },
    {
      header: 'Precio',
      accessor: 'feedData',
      render: (value) => value?.price ? (
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
          {value.price}
        </Typography>
      ) : '-',
    },
    {
      header: 'Stock',
      accessor: 'feedData',
      render: (value) => (
        <StatusBadge
          status={value?.availability === 'in stock' ? 'inStock' : 'outOfStock'}
          label={value?.availability === 'in stock' ? 'En Stock' : 'Sin Stock'}
        />
      ),
    },
    {
      header: 'Enriquecido',
      accessor: 'hasEnrichment',
      render: (value) => (
        <Chip
          label={value ? 'Sí' : 'No'}
          color={value ? 'primary' : 'default'}
          size="small"
          variant={value ? 'filled' : 'outlined'}
        />
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        title="Productos"
        subtitle="Gestiona el catálogo de productos"
        actionLabel="Nuevo Producto"
        actionIcon={<Add />}
        onAction={() => handleOpenDialog()}
        badge={totalCount}
        badgeColor="success"
      >
        <Tooltip title="Enriquecer productos sin datos de catálogo">
          <span>
            <IconButton 
              onClick={handleOpenEnrichDialog}
              disabled={!filterRetail || enriching || getEnrichableCount() === 0}
              color="secondary"
            >
              <AutoAwesome />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Refrescar lista">
          <IconButton onClick={fetchData}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </PageHeader>

      {error && (
        <Fade in>
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        </Fade>
      )}
      {success && (
        <Fade in>
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        </Fade>
      )}

      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <SearchInput
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Buscar por SKU, título o marca..."
          sx={{ minWidth: 280 }}
        />
        <FormControl sx={{ minWidth: 180 }} size="small">
          <InputLabel>Retail</InputLabel>
          <Select
            value={filterRetail}
            label="Retail"
            onChange={(e) => {
              setFilterRetail(e.target.value);
              setFilterCategory('');
              setPage(0);
            }}
          >
            <MenuItem value="">Todos</MenuItem>
            {retails.map((retail) => (
              <MenuItem key={retail.id} value={retail.id}>
                {retail.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 180 }} size="small">
          <InputLabel>Categoría</InputLabel>
          <Select
            value={filterCategory}
            label="Categoría"
            onChange={(e) => {
              setFilterCategory(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="">Todas</MenuItem>
            {filteredCategoriesForRetail.map((category) => (
              <MenuItem key={category.id} value={category.id}>
                {category.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <DataTable
        columns={columns}
        data={products}
        loading={loading}
        onView={(product) => {
          setSelectedProduct(product);
          setViewDialogOpen(true);
        }}
        onEdit={handleOpenDialog}
        onDelete={(product) => {
          setSelectedProduct(product);
          setDeleteDialogOpen(true);
        }}
        emptyMessage="No hay productos registrados"
        emptyDescription="Agrega productos a tu catálogo o impórtalos desde un feed"
        emptyActionLabel="Agregar Producto"
        emptyAction={() => handleOpenDialog()}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={totalCount}
        onPageChange={(newPage) => setPage(newPage)}
        onRowsPerPageChange={(newRowsPerPage) => {
          setRowsPerPage(newRowsPerPage);
          setPage(0);
        }}
      />

      {/* View Product Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Detalles del Producto
          </Typography>
          <IconButton onClick={() => setViewDialogOpen(false)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedProduct && (
            <Grid container spacing={3} sx={{ pt: 1 }}>
              {/* Imagen del producto */}
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Box
                      component="img"
                      src={selectedProduct.feedData?.image || selectedProduct.catalogData?.catalogImage}
                      alt={selectedProduct.feedData?.title || 'Producto'}
                      sx={{ 
                        width: '100%', 
                        maxHeight: 220, 
                        objectFit: 'contain',
                        mb: 2,
                        bgcolor: 'grey.50',
                        borderRadius: 1
                      }}
                      onError={(e) => { e.target.src = 'https://via.placeholder.com/200x200?text=Sin+imagen'; }}
                    />
                    {selectedProduct.hasEnrichment && (
                      <Chip label="Producto Enriquecido" color="primary" size="small" />
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Datos del Feed */}
              <Grid item xs={12} md={8}>
                <Card variant="outlined" sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle2" color="primary" gutterBottom sx={{ fontWeight: 600 }}>
                      Datos del Feed (Retail)
                    </Typography>
                    <Typography variant="h6" gutterBottom>
                      {selectedProduct.feedData?.title || selectedProduct.title || '-'}
                    </Typography>
                    <Grid container spacing={1.5}>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" color="text.secondary">SKU</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{selectedProduct.sku}</Typography>
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" color="text.secondary">Marca</Typography>
                        <Typography variant="body2">{selectedProduct.feedData?.brand || '-'}</Typography>
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" color="text.secondary">GTIN</Typography>
                        <Typography variant="body2">{selectedProduct.feedData?.gtin || '-'}</Typography>
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" color="text.secondary">Precio</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                          {selectedProduct.feedData?.price || '-'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" color="text.secondary">Precio Oferta</Typography>
                        <Typography variant="body2" sx={{ color: 'error.main' }}>
                          {selectedProduct.feedData?.salePrice || '-'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" color="text.secondary">Disponibilidad</Typography>
                        <Box sx={{ mt: 0.5 }}>
                          <StatusBadge
                            status={selectedProduct.feedData?.availability === 'in stock' ? 'inStock' : 'outOfStock'}
                            label={selectedProduct.feedData?.availability === 'in stock' ? 'En Stock' : 'Sin Stock'}
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" color="text.secondary">Cantidad</Typography>
                        <Typography variant="body2">{selectedProduct.feedData?.quantity || '-'}</Typography>
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" color="text.secondary">Condición</Typography>
                        <Typography variant="body2">{selectedProduct.feedData?.condition || '-'}</Typography>
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Typography variant="caption" color="text.secondary">Cuotas</Typography>
                        <Typography variant="body2">
                          {selectedProduct.feedData?.installmentMonths 
                            ? `${selectedProduct.feedData.installmentMonths}x ${selectedProduct.feedData.installmentAmount}` 
                            : '-'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">Categoría</Typography>
                        <Typography variant="body2">{selectedProduct.feedData?.productType || '-'}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">Link</Typography>
                        {selectedProduct.feedData?.link ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography
                              component="a"
                              href={selectedProduct.feedData.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              variant="body2"
                              sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                            >
                              Ver en tienda
                            </Typography>
                            <OpenInNew sx={{ fontSize: 14, color: 'primary.main' }} />
                          </Box>
                        ) : (
                          <Typography variant="body2">-</Typography>
                        )}
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">
                          Última sincronización: {selectedProduct.feedData?.lastSynced 
                            ? new Date(selectedProduct.feedData.lastSynced).toLocaleString('es-ES') 
                            : '-'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Datos del Catálogo (Alquimio) */}
              {selectedProduct.catalogData && (
                <>
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle2" color="secondary" gutterBottom sx={{ fontWeight: 600 }}>
                          Datos del Catálogo (Alquimio)
                        </Typography>
                        <Typography variant="h6" gutterBottom>
                          {selectedProduct.catalogData.name || '-'}
                        </Typography>
                        {selectedProduct.catalogData.catalogDescription && (
                          <Typography variant="body2" paragraph sx={{ whiteSpace: 'pre-line' }}>
                            {selectedProduct.catalogData.catalogDescription}
                          </Typography>
                        )}
                        {selectedProduct.catalogData.videoUrl && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2">
                              <strong>Video:</strong>{' '}
                              <a href={selectedProduct.catalogData.videoUrl} target="_blank" rel="noopener noreferrer">
                                Ver en YouTube
                              </a>
                            </Typography>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Especificaciones del catálogo */}
                  {selectedProduct.catalogData.specifications && Object.keys(selectedProduct.catalogData.specifications).length > 0 && (
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
                            Especificaciones
                          </Typography>
                          <Grid container spacing={1}>
                            {Object.entries(selectedProduct.catalogData.specifications).map(([key, value]) => (
                              <Grid item xs={12} key={key}>
                                <Box sx={{ p: 1, borderRadius: 1, backgroundColor: (theme) => alpha(theme.palette.grey[100], 0.5) }}>
                                  <Typography variant="caption" color="text.secondary">{key}</Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{String(value)}</Typography>
                                </Box>
                              </Grid>
                            ))}
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}

                  {/* Features del catálogo */}
                  {selectedProduct.catalogData.features && selectedProduct.catalogData.features.length > 0 && (
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
                            Características
                          </Typography>
                          {selectedProduct.catalogData.features.map((feature, index) => (
                            <Box key={index} sx={{ mb: 1.5, display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                              {feature.image && (
                                <Box
                                  component="img"
                                  src={feature.image}
                                  alt={feature.title}
                                  sx={{ width: 40, height: 40, objectFit: 'contain', flexShrink: 0, borderRadius: 1 }}
                                />
                              )}
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{feature.title}</Typography>
                                <Typography variant="caption" color="text.secondary">{feature.description}</Typography>
                              </Box>
                            </Box>
                          ))}
                        </CardContent>
                      </Card>
                    </Grid>
                  )}

                  {/* Galería */}
                  {selectedProduct.catalogData.gallery && selectedProduct.catalogData.gallery.length > 0 && (
                    <Grid item xs={12}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
                            Galería de Imágenes
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {selectedProduct.catalogData.gallery.slice(0, 6).map((imgUrl, index) => (
                              <Box
                                key={index}
                                component="img"
                                src={imgUrl}
                                alt={`Imagen ${index + 1}`}
                                sx={{ 
                                  width: 80, 
                                  height: 80, 
                                  objectFit: 'contain',
                                  borderRadius: 1,
                                  bgcolor: 'grey.100',
                                  cursor: 'pointer',
                                  '&:hover': { opacity: 0.8 }
                                }}
                                onClick={() => window.open(imgUrl, '_blank')}
                              />
                            ))}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}
                </>
              )}

              {/* Especificaciones Personalizadas */}
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ borderColor: 'primary.main', borderWidth: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Settings color="primary" />
                        <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600 }}>
                          Especificaciones Personalizadas
                        </Typography>
                        <Chip 
                          label={Object.keys(selectedProduct.specs || {}).length} 
                          size="small" 
                          color="primary" 
                        />
                      </Box>
                      <Button
                        variant="contained"
                        startIcon={<Edit />}
                        size="small"
                        onClick={() => setSpecsEditorOpen(true)}
                      >
                        Editar Specs
                      </Button>
                    </Box>
                    
                    {selectedProduct.specs && Object.keys(selectedProduct.specs).length > 0 ? (
                      <Grid container spacing={1}>
                        {Object.entries(selectedProduct.specs).map(([key, value]) => (
                          <Grid item xs={12} sm={6} md={4} key={key}>
                            <Box sx={{ p: 1.5, borderRadius: 1, backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.05), border: '1px solid', borderColor: 'primary.light' }}>
                              <Typography variant="caption" color="primary.main" sx={{ fontWeight: 500 }}>
                                {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {typeof value === 'boolean' ? (value ? 'Sí' : 'No') : String(value)}
                              </Typography>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    ) : (
                      <Alert severity="info" sx={{ mt: 1 }}>
                        No hay especificaciones personalizadas. Haz clic en "Editar Specs" para agregar.
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Descripción del feed */}
              {selectedProduct.feedData?.description && (
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
                        Descripción del Feed
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-line', maxHeight: 200, overflow: 'auto' }}>
                        {selectedProduct.feedData.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
      </Dialog>

      <FormDialog
        open={dialogOpen}
        title={selectedProduct ? 'Editar Producto' : 'Nuevo Producto'}
        subtitle={selectedProduct ? 'Modifica los datos del producto' : 'Ingresa los datos del nuevo producto'}
        icon={<Inventory />}
        onClose={handleCloseDialog}
        onSubmit={handleSubmit}
        loading={saving}
        maxWidth="md"
      >
        <Grid container spacing={2.5}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="SKU"
              value={formData.sku}
              onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Prodnum"
              value={formData.prodnum}
              onChange={(e) => setFormData({ ...formData, prodnum: e.target.value })}
              fullWidth
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Título"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              fullWidth
              required
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Título Completo"
              value={formData.fullTitle}
              onChange={(e) => setFormData({ ...formData, fullTitle: e.target.value })}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Marca"
              value={formData.brand}
              onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Idioma</InputLabel>
              <Select
                value={formData.lang}
                label="Idioma"
                onChange={(e) => setFormData({ ...formData, lang: e.target.value })}
              >
                <MenuItem value="ES">Español</MenuItem>
                <MenuItem value="PT">Portugués</MenuItem>
                <MenuItem value="EN">Inglés</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Retail</InputLabel>
              <Select
                value={formData.retailId}
                label="Retail"
                onChange={(e) => setFormData({ ...formData, retailId: e.target.value, categoryId: '' })}
              >
                <MenuItem value="">Ninguno</MenuItem>
                {retails.map((retail) => (
                  <MenuItem key={retail.id} value={retail.id}>
                    {retail.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Categoría</InputLabel>
              <Select
                value={formData.categoryId}
                label="Categoría"
                onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
              >
                <MenuItem value="">Ninguna</MenuItem>
                {categories
                  .filter(c => !formData.retailId || c.retailId === formData.retailId)
                  .map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Ranking"
              value={formData.ranking}
              onChange={(e) => setFormData({ ...formData, ranking: e.target.value })}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Cantidad de Reviews"
              type="number"
              value={formData.reviewsCount}
              onChange={(e) => setFormData({ ...formData, reviewsCount: e.target.value })}
              fullWidth
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label="Producto Activo"
            />
          </Grid>
        </Grid>
      </FormDialog>

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Eliminar Producto"
        message={`¿Estás seguro de que deseas eliminar el producto "${selectedProduct?.feedData?.title || selectedProduct?.sku}"?`}
        type="delete"
        onConfirm={handleDelete}
        onCancel={() => setDeleteDialogOpen(false)}
      />

      {/* Enrich Products Dialog */}
      <Dialog
        open={enrichDialogOpen}
        onClose={() => !enriching && setEnrichDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoAwesome color="secondary" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Enriquecer Productos
          </Typography>
        </DialogTitle>
        <DialogContent>
          {!enrichResult ? (
            <Box sx={{ py: 2 }}>
              {enriching ? (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <CircularProgress color="secondary" sx={{ mb: 2 }} />
                  <Typography variant="body1" gutterBottom>
                    Buscando datos en el catálogo de Alquimio...
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Esto puede tomar unos segundos
                  </Typography>
                </Box>
              ) : (
                <>
                  <Typography variant="body1" gutterBottom>
                    Se buscarán datos del catálogo para los productos que aún no tienen información enriquecida.
                  </Typography>
                  <Card variant="outlined" sx={{ mt: 2, bgcolor: 'action.hover' }}>
                    <CardContent>
                      <Typography variant="h4" color="secondary" sx={{ fontWeight: 700 }}>
                        {getEnrichableCount()}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        productos sin enriquecer en {retails.find(r => r.id === filterRetail)?.name || 'el retail seleccionado'}
                      </Typography>
                    </CardContent>
                  </Card>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Los productos se buscarán usando el campo configurado en el retail (MPN, SKU, etc.)
                  </Typography>
                </>
              )}
            </Box>
          ) : (
            <Box sx={{ py: 2 }}>
              <Alert 
                severity={enrichResult.enriched > 0 ? 'success' : 'info'} 
                sx={{ mb: 2 }}
              >
                {enrichResult.enriched > 0 
                  ? `Se enriquecieron ${enrichResult.enriched} productos exitosamente`
                  : 'No se encontraron coincidencias en el catálogo'}
              </Alert>
              
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={4}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                      <Typography variant="h5" color="primary" sx={{ fontWeight: 700 }}>
                        {enrichResult.total}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Procesados
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={4}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                      <Typography variant="h5" color="success.main" sx={{ fontWeight: 700 }}>
                        {enrichResult.enriched}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Enriquecidos
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={4}>
                  <Card variant="outlined">
                    <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                      <Typography variant="h5" color="warning.main" sx={{ fontWeight: 700 }}>
                        {enrichResult.notFound}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        No encontrados
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {enrichResult.enrichedProducts && enrichResult.enrichedProducts.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                    Productos enriquecidos (muestra):
                  </Typography>
                  {enrichResult.enrichedProducts.slice(0, 5).map((p, i) => (
                    <Typography key={i} variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      • {p.sku}: {p.catalogName}
                    </Typography>
                  ))}
                </Box>
              )}

              {enrichResult.notFoundProducts && enrichResult.notFoundProducts.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: 'warning.main' }}>
                    No encontrados en catálogo (muestra):
                  </Typography>
                  {enrichResult.notFoundProducts.slice(0, 5).map((p, i) => (
                    <Typography key={i} variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      • {p.sku}: {p.title?.substring(0, 50)}...
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {!enrichResult ? (
            <>
              <Button 
                onClick={() => setEnrichDialogOpen(false)} 
                disabled={enriching}
              >
                Cancelar
              </Button>
              <Button 
                variant="contained" 
                color="secondary"
                onClick={handleEnrichProducts}
                disabled={enriching || getEnrichableCount() === 0}
                startIcon={enriching ? <CircularProgress size={16} color="inherit" /> : <AutoAwesome />}
              >
                {enriching ? 'Procesando...' : 'Enriquecer'}
              </Button>
            </>
          ) : (
            <Button 
              variant="contained" 
              onClick={() => setEnrichDialogOpen(false)}
            >
              Cerrar
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Specs Editor Dialog */}
      <SpecsEditorDialog
        open={specsEditorOpen}
        onClose={() => setSpecsEditorOpen(false)}
        product={selectedProduct}
        onSave={handleSaveSpecs}
        saving={savingSpecs}
      />
    </Box>
  );
};

export default ProductsPage;
